import { type ApiUnbilledContainerRentGroup } from '@Model';
import { useCallback, useMemo, useState } from 'react';
import {
    isRentGroupSelected,
    stillListedRent,
    withTickedRentGroups,
    withTickedRentLines,
} from './unbilledContainerRentSelection';
import { type RowSelector } from './useContainerSelection';

type TickedIds = string[] | ((current: string[]) => string[]);

export interface UnbilledContainerRentSelection {
    /** The standing times that are selected. */
    selected: ReadonlySet<string>;
    /** What the table of groups selects by: a group is ticked where all its lines are selected. */
    groupSelector: RowSelector;
    /** What the table of a group's lines selects by. */
    lineSelectorOf: (group: ApiUnbilledContainerRentGroup) => RowSelector;
    /** Deselects everything. */
    clear: () => void;
    /** Takes note of the groups that are shown and deselects the standing times that are gone. */
    track: (groups: readonly ApiUnbilledContainerRentGroup[]) => void;
}

const resolved = (ticked: TickedIds, current: string[]) => (typeof ticked === 'function' ? ticked(current) : ticked);

/** The standing times with unbilled rent that are selected for booking. */
// @ai-generated 2026-10-21T14:02:47+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useUnbilledContainerRentSelection(): UnbilledContainerRentSelection {
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const [groups, setGroups] = useState<readonly ApiUnbilledContainerRentGroup[]>([]);

    const track = useCallback((shown: readonly ApiUnbilledContainerRentGroup[]) => {
        setGroups(shown);
        setSelected((current) => stillListedRent(current, shown));
    }, []);

    const clear = useCallback(() => setSelected(new Set()), []);

    const groupSelector = useMemo<RowSelector>(
        () => ({
            selectedObjectIds: groups.filter((group) => isRentGroupSelected(group, selected)).map((group) => group.id),
            setSelectedObjectIds: (ticked: TickedIds) =>
                setSelected((current) =>
                    withTickedRentGroups(
                        current,
                        groups,
                        resolved(
                            ticked,
                            groups.filter((group) => isRentGroupSelected(group, current)).map((group) => group.id),
                        ),
                    ),
                ),
        }),
        [groups, selected],
    );

    const lineSelectorOf = useCallback(
        (group: ApiUnbilledContainerRentGroup): RowSelector => ({
            selectedObjectIds: group.lines.filter((line) => selected.has(line.id)).map((line) => line.id),
            setSelectedObjectIds: (ticked: TickedIds) =>
                setSelected((current) =>
                    withTickedRentLines(
                        current,
                        group,
                        resolved(
                            ticked,
                            group.lines.filter((line) => current.has(line.id)).map((line) => line.id),
                        ),
                    ),
                ),
        }),
        [selected],
    );

    return useMemo(
        () => ({ selected, groupSelector, lineSelectorOf, clear, track }),
        [selected, groupSelector, lineSelectorOf, clear, track],
    );
}
